import { Link } from "react-router-dom";
import { DropdownMenu, Icon, Loading, Toggle, Tooltip, Typography } from "@/components/atoms";
import { DataTableColumnDef } from "@/components/molecules";
import { FleetDriver } from "@/redux/slices/driver/types";
import { clsx, getAssetUrl } from "@/utils";
import { DriverStatusBadge } from "../common/DriverStatusBadge";
import { ActiveStatuses, SuspendedStatuses } from "./types";
import { MyDriversTableData } from "./types";

/**
 * Display-layer mapping for `account_belongs_to.status` values shown to operators.
 *
 * Two concerns folded into one helper:
 *
 * 1. **Legacy → canonical rollup.** The status enum carries older Title Case values
 *    ("Pending Qualification", "Approved", "Retired", "Permanently Terminated",
 *    "Temporarily Suspended", "inactive") from before the lowercase taxonomy was
 *    introduced. No new driver row is written with these strings anymore — they
 *    only persist on legacy data. We roll them up to the canonical lowercase
 *    values so the operator UI shows consistent wording regardless of when the
 *    row was created.
 *
 * 2. **Canonical → display label.** The canonical value `'removed'` surfaces in
 *    the operator UI as `'Terminated'`. Backend value stays `'removed'`;
 *    only the operator-facing label moves.
 *
 * Canonical lowercase values (`pending`, `active`, `suspended`, `left`) and
 * `'Declined'` pass through unchanged. The existing `capitalize` CSS class on
 * the rendering Typography handles first-letter casing.
 */
export const formatStatusLabel = (status: string): string => {
  switch (status) {
    // Canonical → display label
    case "removed":
      return "Terminated";

    // Legacy pending bucket → canonical 'pending'
    case "Pending Qualification":
    case "Application in Progress":
    case "Pending Approval":
      return "pending";

    // Legacy active bucket → canonical 'active'
    case "Approved":
      return "active";

    // Legacy suspended bucket → canonical 'suspended'
    case "Temporarily Suspended":
      return "suspended";

    // Legacy left bucket → canonical 'left'
    case "Retired":
    case "Permanently Terminated":
    case "inactive":
      return "left";

    default:
      return status;
  }
};

export const generateMyDriversTableColumns = () => {
  const columns: DataTableColumnDef<MyDriversTableData>[] = [
    { accessorKey: "name", header: "Name", cell: (props) => props.row.original.name, id: "name", size: 220 },
    {
      accessorKey: "email",
      header: "Email",
      cell: (props) => props.row.original.email,
      id: "email",
      size: 200,
    },
    {
      accessorKey: "vehicle",
      header: "Vehicle",
      cell: (props) => props.row.original.vehicle,
      enableSorting: false,
      size: 185,
    },
    {
      accessorKey: "mobile",
      header: "Mobile",
      cell: (props) => props.row.original.mobile,
      enableSorting: false,
      size: 150,
    },
    {
      accessorKey: "rating",
      header: "Rating",
      cell: (props) => props.row.original.rating,
      id: "rating",
    },
    {
      accessorKey: "state",
      header: "State",
      cell: (props) => props.row.original.state,
      enableSorting: false,
    },
    {
      accessorKey: "tier",
      header: "Tier",
      cell: (props) => props.row.original.tier,
      id: "tier",
    },
    {
      accessorKey: "fleetLeader",
      header: FleetLeaderHeader,
      cell: (props) => props.row.original.fleetLeader,
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (props) => props.row.original.status,
      enableSorting: false,
      size: 160,
    },
    {
      accessorKey: "action",
      header: "Action",
      cell: (props) => props.row.original.action,
      size: 80,
      enableSorting: false,
    },
  ];

  return columns;
};

const fleetLeaderTooltipContent = (
  <div className="flex flex-col gap-3 p-1">
    <Typography variant="paragraph" className="text-white">
      Fleet leaders are defined by operators and have an elevated set of permissions.
    </Typography>
    <div>
      <Typography variant="paragraph" className="text-white">
        By being a fleet leader, they are able to:
      </Typography>
      <ol className="mt-1 ml-4 list-decimal">
        <li><Typography variant="paragraph" className="text-white">Reassign jobs to other driver (via the driver app)</Typography></li>
        <li><Typography variant="paragraph" className="text-white">Be assigned jobs through the private network</Typography></li>
        <li><Typography variant="paragraph" className="text-white">Are visible to operators when searching in the Driver Manager</Typography></li>
      </ol>
    </div>
    <Typography variant="paragraph" className="text-info-light">
      A Fleet Leader can be identified as having a green circle around their profile picture
    </Typography>
  </div>
);

const FleetLeaderHeader = () => {
  return (
    <div className="flex flex-row items-center gap-1.5">
      <Typography variant="small">Fleet Leader</Typography>
      <Tooltip content={fleetLeaderTooltipContent} placement="bottom" interactive maxWidth={350}>
        <Icon isCustom name="help-circle" size="sm" className="cursor-pointer text-neutral-dark-gray" />
      </Tooltip>
    </div>
  );
};

export const generateMyDriversTableData = (
  items: FleetDriver[],
  onUnsuspendDriver: (id: string) => void,
  toggleFleetLeader: (id: string, isFleetLeader: boolean) => void,
  isLoadingToggle: boolean
) => {
  const data = items.map(
    (i) =>
      ({
        name: renderDriverName(i),
        email: i.pendingEmail ? (
          <Typography variant="paragraph" className="break-all">
            <span className="text-warning">{i.pendingEmail}</span>
            <Tooltip content="The driver will need to log in using this email to confirm the change." placement="right">
              <Icon name="Danger" variant="Bold" size="md" className="ml-1 inline-block align-middle text-warning" />
            </Tooltip>
          </Typography>
        ) : (
          i.email
        ),
        vehicle: [i.vehicle?.make, i.vehicle?.model].filter(Boolean).join(" ") || "-",
        mobile: i.phone,
        rating: renderRating(i.metadata.rating),
        state: i.state ? i.state : "-",
        tier: i.tier || i.tier === 0 ? `${i.tier}` : "-",
        fleetLeader: (
          <div className="flex flex-row items-center gap-1.5">
            {i.isFleetLeader ? (
              <Icon name="TickCircle" variant="Bold" size="sm" className="text-success" />
            ) : (
              <Icon name="CloseCircle" variant="Bold" size="sm" className="text-neutral-dark-gray" />
            )}
            <Typography variant="paragraph">{i.isFleetLeader ? "Yes" : "No"}</Typography>
          </div>
        ),
        status: renderFleetStatus(i.status),
        action: renderMyDriverActions(i, onUnsuspendDriver, toggleFleetLeader, isLoadingToggle),
      } as MyDriversTableData)
  );

  return data;
};

const renderMyDriverActions = (
  driver: FleetDriver,
  onUnsuspendDriver: (id: string) => void,
  toggleFleetLeader: (id: string, isFleetLeader: boolean) => void,
  isLoadingToggleFleetLeader: boolean
) => {
  const { status } = driver;
  const isActiveDriver = ActiveStatuses.includes(status);
  const isSuspendedDriver = SuspendedStatuses.includes(status);

  return (
    <DropdownMenu button={<Icon name="options" isCustom size="lg" />} position="bottom-start" className="!flex max-w-[24px]">
      <Link to={driver.uuid}>
        <DropdownMenu.Item>
          <Icon name="UserSquare" size="sm" className="mr-2.5" />
          <Typography variant="paragraph">Manage Driver</Typography>
        </DropdownMenu.Item>
      </Link>
      {isSuspendedDriver && (
        <DropdownMenu.Item onClick={() => onUnsuspendDriver(driver.uuid)}>
          <Icon name="Refresh2" size="sm" className="mr-2.5" />
          <Typography variant="paragraph">Unsuspend Driver</Typography>
        </DropdownMenu.Item>
      )}
      {isActiveDriver && (
        <DropdownMenu.Item className="relative" disabled>
          {isLoadingToggleFleetLeader && <Loading />}
          <Toggle
            className="mr-2.5"
            size="xs"
            checked={driver.isFleetLeader}
            onChange={(checked) => toggleFleetLeader(driver.uuid, checked)}
            disabled={isLoadingToggleFleetLeader}
          />
          <Typography variant="paragraph">Fleet Leader</Typography>
        </DropdownMenu.Item>
      )}
    </DropdownMenu>
  );
};

export const renderDriverName = (driver: FleetDriver) => {
  return (
    <Link to={driver.uuid}>
      <div className="flex flex-row items-center gap-2.5 hover:text-info">
        {renderFleetAvatar(driver.avatar, driver.name, driver.isFleetLeader)}
        <Typography variant="paragraph">{driver.name}</Typography>
        <DriverStatusBadge status={driver.status} licenceVerificationStatus={driver.licence?.verificationStatus} />
      </div>
    </Link>
  );
};

export const renderFleetAvatar = (avatar: string | null, name: string, isFleetLeader: boolean) => (
  <div className="ml-1">
    {avatar ? (
      <div className={clsx("h-6 w-6 rounded-full", { "ring-2 ring-success ring-offset-2": isFleetLeader })}>
        <img className="rounded-full" src={getAssetUrl(avatar, { height: 24, width: 24, fit: "cover" })} alt={name} />
      </div>
    ) : (
      <div
        className={clsx("h-6 w-6 rounded-full bg-neutral-mid-gray", {
          "ring-2 ring-success ring-offset-2": isFleetLeader,
        })}
      />
    )}
  </div>
);

export const renderFleetStatus = (status: string) => {
  switch (status) {
    case "pending":
    case "Pending Qualification":
    case "Application in Progress":
    case "Pending Approval":
      return (
        <Typography variant="paragraph" className="flex flex-row items-center gap-2 capitalize">
          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-neutral-mid-gray" /> {formatStatusLabel(status)}
        </Typography>
      );
    case "active":
    case "Approved":
      return (
        <Typography variant="paragraph" className="flex flex-row items-center gap-2 capitalize">
          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-success" /> {formatStatusLabel(status)}
        </Typography>
      );
    case "Declined":
    case "removed":
    case "left":
    case "inactive":
    case "Permanently Terminated":
    case "Retired":
    case "suspended":
    case "Temporarily Suspended":
    case "License Expired":
      return (
        <Typography variant="paragraph" className="flex flex-row items-center gap-2 capitalize">
          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-danger" /> {formatStatusLabel(status)}
        </Typography>
      );
    default:
      return (
        <Typography variant="paragraph" className="flex flex-row items-center gap-2 capitalize">
          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-neutral-dark-gray" /> {formatStatusLabel(status)}
        </Typography>
      );
  }
};

export const renderRating = (rating?: number | null) => (
  <div className="flex flex-row items-center gap-x-1.5">
    <Typography variant="paragraph">{rating || "-"}</Typography>
    {rating && <Icon size="md" name="Star1" className="fill-neutral-dark-gray text-neutral-dark-gray" />}
  </div>
);
